/**
 * Appointment finish path — Firestore update builders (SLaM / 3 Borough).
 *
 * At the end of the LAST interview module of an appointment the Lead clinician
 * chooses one of two paths (see InterviewFinishPathDialog):
 *
 *   Path 1 "Share the outcome now"  -> buildPadInAppointmentUpdates()
 *     The clinician tells the patient the provisional ADHD outcome and starts
 *     the Post-Assessment Discussion in the same appointment. We record the
 *     path, store the provisional outcome on the PAD stage, mark the interview
 *     module complete and its report generated, and unlock DD/PAD if they are
 *     still locked. The formal Diagnostic Decision is completed later.
 *
 *   Path 2 "End the appointment here" -> buildDeferPadUpdates()
 *     The clinician ends the appointment and generates the interview report;
 *     DD and PAD happen later in their own time. Only the path is recorded;
 *     the module completion + report-generated fields are written by the
 *     module's normal "Generate report" flow.
 *
 * Which module asks the question is decided by wf.appointmentFinishPathModule:
 *   'ci'  — two-clinician SLaM (CI Review & Synthesis, 12_DF.js)
 *   'ssi' — one-clinician SLaM (SSI Review & Synthesis, 3_SSIReview.js)
 *
 * Both pages build their transaction update map from these helpers so the two
 * entry points cannot drift apart. The helpers are pure (they only read the
 * patient document passed in and return a plain object of dot-notation keys)
 * apart from the Firestore `serverTimestamp()` sentinels they embed, which is
 * why unit tests mock `firebase/firestore` at the boundary.
 *
 * The decision key keeps its historical name `workflowDecisions.ciFinishPath`
 * so every downstream reader (patient overview ordering, PAD provisional
 * outcome, DD mismatch dialog, backend pad_outcome_utils) works unchanged for
 * both generations of patient.
 */

import { serverTimestamp } from 'firebase/firestore';
import { CI_FINISH_PATHS } from '../constants/workflowConfig';
import {
  buildFirstCompletedFieldsDotNotation,
  buildFirstGeneratedFieldsDotNotation,
} from './reportAuditHelpers';
import { resolveDdPadUnlockOnCiComplete } from './moduleUnlockUtils';

/**
 * Maps a stage key in `dataModel` to the prefix used by the report fields in
 * `6_manageReports` (e.g. `ciReportGenerated`, `ssiReportFirstGeneratedAt`).
 */
export const FINISH_PATH_MODULES = Object.freeze({
  ci: Object.freeze({
    stageKey: '2_clinicalInterview',
    reportPrefix: 'ci',
  }),
  ssi: Object.freeze({
    stageKey: '3_semiStructuredInterview',
    reportPrefix: 'ssi',
  }),
});

/**
 * Path 2 — record that the clinician is deferring the PAD. Patient overview
 * flips to show DD ahead of PAD.
 *
 * @returns {Object} dot-notation update map for `transaction.update`
 */
export function buildDeferPadUpdates() {
  return {
    'workflowDecisions.ciFinishPath': CI_FINISH_PATHS.DEFER_PAD,
  };
}

/**
 * Path 1 — share the outcome now and start the PAD in this appointment.
 *
 * @param {Object} args
 * @param {'ci'|'ssi'} args.module  Which interview module is finishing. Picks
 *   the stage key and the report prefix (see FINISH_PATH_MODULES).
 * @param {Object|null} args.patientDataModel  The full patient document, used
 *   to decide whether first-completion / first-generation stamps are needed and
 *   whether DD / PAD are still locked.
 * @param {{uid: string, displayName?: string}} args.user  The signed-in clinician.
 * @param {string} args.provisionalAdhdOutcome  'positive' | 'negative' as chosen
 *   in the dialog; stored on the PAD stage for the PAD review page to read.
 * @returns {{ updates: Object, didWriteFirstCompleted: boolean, isFirstGeneration: boolean }}
 *   `updates` is the dot-notation map for `transaction.update`. The two
 *   booleans tell the caller what to pass to the audit helpers
 *   (`appendReportAuditEntry` and `recordInferredAppointmentDateOnCompletion`).
 */
export function buildPadInAppointmentUpdates({
  module,
  patientDataModel,
  user,
  provisionalAdhdOutcome,
}) {
  const moduleConfig = FINISH_PATH_MODULES[module];
  if (!moduleConfig) {
    throw new Error(`buildPadInAppointmentUpdates: unknown module "${module}"`);
  }
  const { stageKey, reportPrefix } = moduleConfig;

  const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
  const stage = patientDataModel?.dataModel?.[stageKey];
  const { unlockPad, unlockDd } = resolveDdPadUnlockOnCiComplete(patientDataModel);

  // Captured separately (not just spread) so the caller can tell the
  // appointment-date audit whether this write was the FIRST completion.
  const firstCompletedFields = buildFirstCompletedFieldsDotNotation(stageKey, stage);
  const isFirstGeneration = !manageReports?.[`${reportPrefix}ReportFirstGeneratedAt`];

  const updates = {
    'workflowDecisions.ciFinishPath': CI_FINISH_PATHS.PAD_IN_APPOINTMENT,
    'dataModel.5_postAssessmentDiscussion.provisionalAdhdOutcome': provisionalAdhdOutcome,
    [`dataModel.${stageKey}.status`]: 'completed',
    [`dataModel.${stageKey}.progress`]: 100,
    [`dataModel.${stageKey}.docs_available`]: true,
    [`dataModel.${stageKey}.lastCompletedAt`]: serverTimestamp(),
    [`dataModel.${stageKey}.lastCompletedBy`]: user?.uid ?? null,
    [`dataModel.${stageKey}.lastCompletedByName`]: user?.displayName ?? null,
    ...firstCompletedFields,
    [`dataModel.6_manageReports.${reportPrefix}ReportGenerated`]: true,
    [`dataModel.6_manageReports.${reportPrefix}ReportGeneratedAt`]: serverTimestamp(),
    [`dataModel.6_manageReports.${reportPrefix}ReportGeneratedBy`]: user?.uid ?? null,
    [`dataModel.6_manageReports.${reportPrefix}ReportGeneratedByName`]: user?.displayName ?? null,
    ...buildFirstGeneratedFieldsDotNotation(reportPrefix, manageReports),
  };

  // Unlock PAD/DD only when still locked (SLaM: modules open as soon as the
  // interviews are complete; report approval stays a Documents gate only). We
  // deliberately do NOT stamp any DD firstCompletedAt / appointment date here:
  // DD is done later in the clinician's own time, so its date must only ever
  // be inferred from DD's own completion (or set manually).
  if (unlockPad) {
    updates['dataModel.5_postAssessmentDiscussion.status'] = 'notStarted';
  }
  if (unlockDd) {
    updates['dataModel.4_diagnosticDecision.status'] = 'notStarted';
  }

  return {
    updates,
    didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
    isFirstGeneration,
  };
}
